/**
 * Utility functions for PO Lines Tab
 */
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { ACKNOWLEDGE_STATUS_CONFIG, PurchaseOrderLine, StatusConfig } from './types';
import type { User } from '../../../../lib/api';

/**
 * True when the supplier has covered the remaining outstanding qty for
 * this line entirely from their own warehouse stock (delivered_warehouse_qty
 * >= outstanding_qty). Equivalent to received_qty + warehouse_qty >= quantity:
 * everything that hasn't been GRN'd yet is sitting in the supplier's
 * warehouse ready to dispatch.
 *
 * Drives the supplier-only "In Stock" grey-out: lines in this state are
 * no longer actionable by the supplier (Accept / Propose / Hold suppressed)
 * but stay visible until the customer GRNs. Caller is responsible for the
 * isSupplier check — this predicate is portal-agnostic.
 */
export function isFullyInSupplierWarehouse(line: PurchaseOrderLine): boolean {
  const outstanding = parseFloat(line.outstanding_qty || '0') || 0;
  const inStock = parseFloat(line.delivered_warehouse_qty || '0') || 0;
  return outstanding > 0 && inStock >= outstanding;
}

/**
 * Get sort icon based on current sort state
 */
export function getSortIcon(
  field: string,
  sortField: string | null,
  sortDirection: 'asc' | 'desc'
) {
  if (sortField !== field) {
    return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

/**
 * Get acknowledge status badge element
 */
export function getAcknowledgeStatusBadge(status: string) {
  const config: StatusConfig = ACKNOWLEDGE_STATUS_CONFIG[status] || {
    label: status || 'Unknown',
    className: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

/**
 * Check if user can release a hold on a line.
 * Admins and staff can always release holds.
 * Customers and suppliers can release holds they placed.
 * Falls back to true if no user is provided (backwards compatibility).
 */
export function canReleaseHold(user?: User | null, line?: { on_hold_by_company_type?: string | null }): boolean {
  if (!user) return true;

  // Only the side that placed a hold may release it. Mirrors the rule already
  // applied to schedule rows (tabs/schedule-lines/ScheduleLineRow) and enforced
  // server-side in PurchaseOrderLineWorkflowService.release_hold.
  //
  // Previously this took only `user` and returned true on every branch, so a
  // supplier could release a hold the customer had placed and vice versa.
  const placedBy = line?.on_hold_by_company_type;

  // Legacy rows with no recorded placer stay releasable by either side, so
  // pre-existing holds cannot become permanently stuck.
  if (!placedBy) return true;

  const isSupplier = user.platform === 'SUPPLIER';
  // get_effective_company_type writes 'helix' for the Helix tenant; treat it as
  // customer-side, matching how getNextAction reads the same field.
  return isSupplier ? placedBy === 'supplier' : (placedBy === 'customer' || placedBy === 'helix');
}
